import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { ArrowRight, Building2, FileText, HandshakeIcon, Heart, Layers, Network } from 'lucide-react';
import organization from '../content/organization.json';

const actionClass = 'inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950';

const ecosystem = [
  {
    icon: Building2,
    title: 'OBJECTIF SCOP OUTREMER',
    role: 'La coopérative',
    description: 'O’SCOP est une société coopérative d’intérêt collectif constituée en SAS à capital variable. Sa gouvernance et son organisation sont définies par ses statuts.',
    to: '/qui-sommes-nous',
    label: 'Comprendre la coopérative',
    gradient: 'from-amber-600 to-orange-700',
  },
  {
    icon: Layers,
    title: 'Les branches d’activité',
    role: 'Les domaines de services',
    description: 'Les branches organisent les domaines d’activité de la coopérative, dont le bâtiment, la formation et la logistique. Elles permettent de repérer le parcours adapté à votre besoin.',
    to: '/etablissements',
    label: 'Découvrir les branches',
    gradient: 'from-blue-600 to-cyan-700',
  },
  {
    icon: HandshakeIcon,
    title: 'COPPAM',
    role: 'Un partenaire distinct',
    description: 'COPPAM intervient dans l’accueil, l’accompagnement et la médiation. Sa coopération avec O’SCOP s’inscrit dans un cadre conventionnel, avec des responsabilités propres à chaque structure.',
    to: '/garanties/coppam',
    label: 'Comprendre le rôle de COPPAM',
    gradient: 'from-green-600 to-emerald-700',
  },
  {
    icon: FileText,
    title: 'FOGEDOM-SCIC',
    role: 'Le fonds interne de la SCIC',
    description: 'FOGEDOM-SCIC est un fonds de soutien, de prévention et d’ingénierie constitué au sein d’OBJECTIF SCOP OUTREMER, sans personnalité juridique distincte. Ses interventions relèvent des statuts et de son règlement.',
    to: '/fogedom/presentation',
    label: 'Découvrir le fonds interne',
    gradient: 'from-purple-600 to-pink-700',
  },
];

export default function APropos() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="a-propos-page">
      <Navigation />
      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-28 md:pt-32 pb-16">
          <div aria-hidden="true" className="absolute -left-32 top-12 h-96 w-96 rounded-full bg-emerald-600/15 blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8"><BackButton to="/" label="Retour à l’accueil" /></div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/25 px-4 py-2 text-sm text-emerald-200 mb-6"><Network aria-hidden="true" className="h-4 w-4" /> À propos</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6 max-w-4xl">Comprendre l’écosystème <span className="text-emerald-300">O’SCOP</span></h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-4xl mb-8">{organization.legalName} articule des branches d’activité, des dispositifs internes et des coopérations avec des partenaires. Cette page vous aide à identifier leurs rôles et à choisir votre parcours.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className={`${actionClass} bg-emerald-600 text-white hover:bg-emerald-500`}>Explorer les services <ArrowRight aria-hidden="true" className="w-4 h-4" /></Link>
              <Link to="/contact" className={`${actionClass} border border-slate-600 text-white hover:bg-slate-800`}>Présenter mon besoin</Link>
            </div>
          </div>
        </section>

        <section className="py-14 bg-slate-900/50 border-y border-slate-800" aria-labelledby="roles-title">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="roles-title" className="text-3xl font-bold mb-4">Des rôles complémentaires</h2>
            <p className="text-slate-300 max-w-3xl mb-9 leading-relaxed">Une branche, un fonds interne et une organisation partenaire n’ont pas le même statut. Les pages dédiées présentent leurs fonctions et le cadre des démarches proposées.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {ecosystem.map(({ icon: Icon, title, role, description, to, label, gradient }) => (
                <article key={title} className="rounded-2xl border border-slate-700 bg-slate-900 p-7 flex flex-col">
                  <div aria-hidden="true" className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-5`}><Icon className="h-7 w-7 text-white" /></div>
                  <p className="text-sm text-emerald-300 mb-2">{role}</p>
                  <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
                  <p className="text-slate-300 leading-relaxed mb-6">{description}</p>
                  <Link to={to} className="inline-flex items-center gap-2 mt-auto text-emerald-300 hover:text-emerald-200 font-semibold">{label} <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" aria-labelledby="dispositifs-title">
          <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-7 md:p-10">
            <FileText aria-hidden="true" className="h-8 w-8 text-amber-300 mb-5" />
            <h2 id="dispositifs-title" className="text-3xl font-bold mb-4">Comprendre les dispositifs avant de s’engager</h2>
            <p className="text-slate-300 leading-relaxed max-w-4xl mb-4">Les pages consacrées au bâtiment présentent plusieurs dispositifs et parcours d’accompagnement. Leurs noms et leurs objectifs ne suffisent pas à déterminer les conditions applicables à un projet.</p>
            <p className="text-slate-300 leading-relaxed max-w-4xl mb-7">Consultez les documents qui précisent le périmètre, les bénéficiaires, les exclusions et les modalités d’intervention. Une simulation, une demande d’information ou le dépôt d’un dossier ne valent pas accord d’intervention.</p>
            <div className="flex flex-wrap gap-5">
              <Link to="/garanties-solidaires-btp" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-semibold">Consulter la présentation des dispositifs <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
              <Link to="/contact" className="text-amber-300 hover:text-amber-200 font-semibold underline underline-offset-4">Demander les conditions adaptées à mon projet</Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" aria-labelledby="parcours-title">
          <h2 id="parcours-title" className="text-3xl font-bold mb-7">Choisir votre démarche</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Rechercher un service', text: 'Précisez votre activité, votre territoire et le besoin à traiter.', to: '/services', label: 'Voir les services' },
              { title: 'Proposer un soutien', text: 'Présentez la contribution financière, matérielle ou en compétences que vous envisagez.', to: '/devenir-sponsor', label: 'Accéder au parcours sponsor' },
              { title: 'Coopérer avec O’SCOP', text: 'Exposez une proposition de partenariat opérationnel ou demandez des informations sur le sociétariat.', to: '/contact', label: 'Contacter l’équipe' },
            ].map((item) => <article key={item.title} className="rounded-2xl border border-slate-800 bg-slate-900 p-6 flex flex-col"><Heart aria-hidden="true" className="h-6 w-6 text-emerald-300 mb-4" /><h3 className="text-xl font-bold mb-3">{item.title}</h3><p className="text-slate-300 leading-relaxed text-sm mb-6">{item.text}</p><Link to={item.to} className="inline-flex items-center gap-2 mt-auto text-emerald-300 hover:text-emerald-200 font-semibold">{item.label} <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link></article>)}
          </div>
          <p className="mt-8 text-sm text-slate-400 max-w-4xl">Les conditions d’accès aux services, de soutien et d’admission comme associé relèvent de démarches distinctes. Retrouvez l’identité juridique de la coopérative dans les <Link to="/mentions-legales" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-4">mentions légales</Link>.</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
